import { useForm } from '@mantine/form';
import { DEFAULT_SMART_ASSISTANT_COLOR_THEME } from 'shared/constants/smart-assistant';
import {
  ConfigurationFormValues,
  CustomizationFormValues,
  DiscordIntegartionFormValues,
  FacebookIntegrationFormValues,
  InstagramIntegrationFormValues,
  SmartAssistant,
  SmartAssistantModel,
  WhatsAppIntegrationFormValues
} from 'shared/interfaces/SmartAssistant';
import {
  CUSTOM_PROMPT_MAX_LENGTH,
  DISCORD_MAX_LENGTH_COMMAND,
  SMART_ASSISTANT_NAME_MAX_LENGTH,
  maxLengthValidator,
  requiredInputValidator,
  validateDiscordInviteLink
} from 'shared/utils/form-validators';

export function useMessagingSmartAssistantForms(smartAssistant?: SmartAssistant) {
  const configurationForm = useForm<ConfigurationFormValues>({
    initialValues: {
      customSystemPrompt: smartAssistant?.customSystemPrompt ?? '',
      enabledEmoji: smartAssistant?.enabledEmoji ?? false,
      model: smartAssistant?.model ?? SmartAssistantModel.GPT_4_O_MINI,
      restrictAnswersToResourcesContent: smartAssistant?.restrictAnswersToResourcesContentResponse ? true : false,
      restrictAnswersToResourcesContentResponse: smartAssistant?.restrictAnswersToResourcesContentResponse ?? ''
    } as ConfigurationFormValues,
    validate: {
      customSystemPrompt: (value) =>
        requiredInputValidator(value) || maxLengthValidator(value, CUSTOM_PROMPT_MAX_LENGTH),
      model: (value) => requiredInputValidator(value),
      restrictAnswersToResourcesContentResponse: (value, values) =>
        values.restrictAnswersToResourcesContent ? requiredInputValidator(value) : null
    }
  });

  const basicsForm = useForm({
    initialValues: {
      name: smartAssistant?.name ?? '',
      gradientColors: DEFAULT_SMART_ASSISTANT_COLOR_THEME.gradientColors
    } as CustomizationFormValues,
    validate: {
      name: (value) => requiredInputValidator(value) || maxLengthValidator(value, SMART_ASSISTANT_NAME_MAX_LENGTH)
    }
  });

  const facebookForm = useForm({
    initialValues: {
      facebookAccount: smartAssistant?.facebookIntegration?.facebookAccount ?? ''
    } as FacebookIntegrationFormValues,
    validate: {
      facebookAccount: (value) => requiredInputValidator(value)
    }
  });

  const instagramForm = useForm({
    initialValues: {
      instagramAccount: smartAssistant?.instagramIntegration?.instagramAccount ?? ''
    } as InstagramIntegrationFormValues,
    validate: {
      instagramAccount: (value) => requiredInputValidator(value)
    }
  });

  const whatsAppForm = useForm({
    initialValues: {
      whatsAppAccount: smartAssistant?.whatsAppIntegration?.whatsAppAccount ?? '',
      whatsAppPhoneNumberId: smartAssistant?.whatsAppIntegration?.whatsAppPhoneNumberId ?? ''
    } as WhatsAppIntegrationFormValues,
    validate: {
      whatsAppAccount: (value) => requiredInputValidator(value),
      whatsAppPhoneNumberId: (value) => requiredInputValidator(value)
    }
  });

  const discordForm = useForm({
    initialValues: {
      discordInviteLink: smartAssistant?.discordIntegration?.inviteLink ?? '',
      discordInvocationCommand: smartAssistant?.discordIntegration?.invocationCommand ?? ''
    } as DiscordIntegartionFormValues,
    validate: {
      discordInviteLink: (value) => requiredInputValidator(value) || validateDiscordInviteLink(value),
      discordInvocationCommand: (value) =>
        requiredInputValidator(value) || maxLengthValidator(value, DISCORD_MAX_LENGTH_COMMAND)
    }
  });

  return {
    configurationForm,
    basicsForm,
    facebookForm,
    instagramForm,
    whatsAppForm,
    discordForm
  };
}
